import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Image, Upload, Trash2, Copy, Check, Loader2, Play, Search, X } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { cn } from '../components/ui/cn.ts';
import { Pagination } from '../components/ui/Pagination.tsx';
import { AddEmbedModal } from '../components/ui/AddEmbedModal.tsx';
import {
  type MediaAsset,
  type MediaListResponse,
  ALLOWED_IMAGE_TYPES,
  ALLOWED_VIDEO_TYPES,
  ALLOWED_TYPES,
  MAX_IMAGE_BYTES,
  MAX_VIDEO_BYTES,
  thumbUrl,
  formatBytes,
  getAssetBadge,
  getAssetIcon,
  isEmbed,
} from '../lib/media.ts';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function fmtDuration(secs: number): string {
  const totalSecs = Math.floor(secs);
  const m = Math.floor(totalSecs / 60);
  const s = totalSecs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ─── Asset card ───────────────────────────────────────────────────────────────

function AssetCard({
  asset,
  onDelete,
  onCopy,
  copiedId,
  onClick,
}: {
  asset: MediaAsset;
  onDelete: (id: string) => void;
  onCopy: (url: string, id: string) => void;
  copiedId: string | null;
  onClick: (id: string) => void;
}) {
  const isCopied = copiedId === asset.id;
  const isPending = asset.thumbnailStatus === 'pending';
  const isVideo = asset.assetType === 'video';
  const isEmbedAsset = isEmbed(asset);
  const isDocument = asset.assetType === 'document';
  const thumb = thumbUrl(asset);
  const badge = getAssetBadge(asset);
  const icon = getAssetIcon(asset);

  return (
    <div
      className="group relative rounded-xl border border-zinc-200 overflow-hidden bg-white hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onClick(asset.id)}
    >
      {/* Thumbnail */}
      <div className="aspect-square bg-zinc-50 flex items-center justify-center overflow-hidden">
        {thumb ? (
          <img
            src={thumb}
            alt={asset.fileName}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <span className="text-4xl select-none">{icon}</span>
        )}
      </div>

      {/* Asset type badge */}
      <div className={`absolute top-2 left-2 px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${badge.color}`}>
        {badge.label}
      </div>

      {/* Video play badge */}
      {isVideo && (
        <div className="absolute inset-x-0 top-0 bottom-[52px] flex items-center justify-center pointer-events-none">
          <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
            <Play size={16} className="text-white ml-0.5" fill="white" />
          </div>
        </div>
      )}

      {/* Embed link icon overlay */}
      {isEmbedAsset && !thumb && (
        <div className="absolute inset-x-0 top-0 bottom-[52px] flex items-center justify-center pointer-events-none">
          <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center text-white text-xl">
            🔗
          </div>
        </div>
      )}

      {/* Thumbnail pending spinner — shown while Lambda generates thumbnails */}
      {isPending && (
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-white/90 rounded-full px-1.5 py-0.5 shadow text-[10px] text-zinc-500 font-medium">
          <Loader2 size={10} className="animate-spin" />
          Processing…
        </div>
      )}

      {/* Hover overlay with actions */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCopy(asset.publicUrl ?? asset.embedUrl ?? '', asset.id);
          }}
          className="p-2 rounded-full bg-white/90 hover:bg-white text-zinc-700 shadow transition-colors"
          title="Copy URL"
        >
          {isCopied ? <Check size={14} /> : <Copy size={14} />}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(asset.id);
          }}
          className="p-2 rounded-full bg-white/90 hover:bg-red-50 text-zinc-700 hover:text-red-600 shadow transition-colors"
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* File info */}
      <div className="px-2.5 py-2 border-t border-zinc-100">
        <p className="text-xs font-medium text-zinc-800 truncate" title={asset.fileName}>
          {asset.fileName}
        </p>
        <p className="text-[10px] text-zinc-400 mt-0.5">
          {isEmbedAsset
            ? (asset.embedProvider ?? 'embed')
            : formatBytes(asset.sizeBytes)}
          {isVideo && asset.duration ? ` · ${fmtDuration(asset.duration)}` : ''}
          {!isDocument && !isEmbedAsset && asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ''}
          {' · '}
          {fmtDate(asset.createdAt)}
        </p>
      </div>
    </div>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 30;

// ─── MediaLibraryPage ─────────────────────────────────────────────────────────

export function MediaLibraryPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'image' | 'video' | 'document' | 'embed'>('all');
  const [embedModalOpen, setEmbedModalOpen] = useState(false);

  // ── Debounced search ─────────────────────────────────────────────────────────
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── Query (server-side pagination + search + filter) ────────────────────────
  const queryParams = new URLSearchParams();
  queryParams.set('limit', String(PAGE_SIZE));
  queryParams.set('offset', String((page - 1) * PAGE_SIZE));
  if (debouncedSearch) queryParams.set('q', debouncedSearch);
  if (typeFilter !== 'all') queryParams.set('type', typeFilter);

  const { data, isLoading, isError } = useQuery<MediaListResponse>({
    queryKey: ['media-assets', { page, q: debouncedSearch, type: typeFilter }],
    queryFn: () => api.get(`/api/v1/media?${queryParams.toString()}`).then((r) => r.data),
    staleTime: 15_000,
    refetchInterval: (query) => {
      const items = (query.state.data as MediaListResponse | undefined)?.items ?? [];
      return items.some((a) => a.thumbnailStatus === 'pending') ? 5_000 : false;
    },
  });

  const pagedAssets = data?.items ?? [];
  const totalItems = (data as any)?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  // ── Delete ───────────────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/media/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['media-assets'] });
      toast.success('Asset deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const handleDelete = (id: string) => {
    const asset = pagedAssets.find((a) => a.id === id);
    if (!asset) return;
    if (!confirm(`Delete "${asset.fileName}"? This cannot be undone.`)) return;
    deleteMutation.mutate(id);
  };

  // ── Copy URL ─────────────────────────────────────────────────────────────────
  const handleCopy = (url: string, id: string) => {
    navigator.clipboard.writeText(url).then(
      () => {
        setCopiedId(id);
        toast.success('URL copied to clipboard');
        setTimeout(() => setCopiedId(null), 2000);
      },
      () => {
        toast.error('Could not copy URL — please copy it manually from the address bar.');
      },
    );
  };

  // ── Upload ───────────────────────────────────────────────────────────────────
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const { data: urlData } = await api.post<{
        uploadUrl: string;
        publicUrl: string;
        assetId: string;
        assetType: string;
      }>('/api/v1/media/upload-url', {
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });

      const res = await fetch(urlData.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error('S3 upload failed');

      try {
        await api.post(`/api/v1/media/${urlData.assetId}/confirm`, { sizeBytes: file.size });
      } catch {
        throw new Error(
          'File uploaded to S3 but could not save metadata. ' +
            'Please refresh and try again, or contact support.',
        );
      }

      return urlData;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['media-assets'] });
      setUploadError(null);
      // Reset the file input so the same file can be uploaded again if needed.
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success('File uploaded — thumbnails generating in background…');
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Upload failed. Please try again.';
      setUploadError(msg);
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.error(msg);
    },
  });

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const file = files[0];

      if (!ALLOWED_TYPES.includes(file.type)) {
        setUploadError(
          `File type '${file.type}' is not supported. ` +
            'Use JPEG, PNG, GIF, WebP, SVG, TIFF, BMP, HEIC/HEIF, or MP4.',
        );
        return;
      }
      const maxBytes = ALLOWED_VIDEO_TYPES.includes(file.type) ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
      if (file.size > maxBytes) {
        setUploadError(
          `File too large (${formatBytes(file.size)}). Maximum size is ${formatBytes(maxBytes)}.`,
        );
        return;
      }

      setUploadError(null);
      uploadMutation.mutate(file);
    },
    [uploadMutation],
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Media Library"
        description="All media uploaded to your tenant — reusable across templates, pages, and collections."
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEmbedModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-zinc-200 text-zinc-700 text-sm font-medium rounded-lg hover:bg-zinc-50 transition-colors"
            >
              🔗 Aggiungi Embed
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploadMutation.isPending ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Upload size={15} />
              )}
              {uploadMutation.isPending ? 'Uploading…' : 'Upload File'}
            </button>
          </div>
        }
      />

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {/* Drop zone wrapper (full page area) */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          'mt-6 min-h-[200px] rounded-2xl border-2 border-dashed transition-colors',
          isDragging ? 'border-zinc-800 bg-zinc-50' : 'border-transparent',
        )}
      >
        {/* Upload error */}
        {uploadError && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            {uploadError}
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-24 text-zinc-400">
            <Loader2 size={28} className="animate-spin mr-3" />
            Loading media library…
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="text-center py-24 text-red-500 text-sm">
            Failed to load media assets. Please refresh.
          </div>
        )}

        {/* Toolbar: search + type filter */}
        {!isLoading && !isError && (
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); }}
                placeholder="Search files…"
                className="h-9 pl-9 pr-8 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 w-56"
              />
              {search && (
                <button onClick={() => { setSearch(''); setPage(1); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-300 hover:text-zinc-500">
                  <X size={13} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-0.5">
              {(
                [
                  { key: 'all', label: 'All' },
                  { key: 'image', label: 'Images' },
                  { key: 'video', label: 'Video' },
                  { key: 'document', label: 'Docs' },
                  { key: 'embed', label: 'Embed' },
                ] as const
              ).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setTypeFilter(key); setPage(1); }}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    typeFilter === key ? 'bg-white text-zinc-800 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {totalItems > 0 && (
              <span className="text-xs text-zinc-400">
                {totalItems} file{totalItems !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && totalItems === 0 && !debouncedSearch && typeFilter === 'all' && (
          <div className="flex flex-col items-center justify-center py-24 text-zinc-400 gap-4">
            <Image size={48} className="opacity-30" />
            <div className="text-center">
              <p className="text-sm font-medium text-zinc-600">No media assets yet</p>
              <p className="text-xs text-zinc-400 mt-1">
                Upload an image or video, or drag and drop it anywhere on this page.
              </p>
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-700 transition-colors"
            >
              Upload your first file
            </button>
          </div>
        )}

        {/* No results after filtering */}
        {!isLoading && !isError && totalItems === 0 && (debouncedSearch || typeFilter !== 'all') && (
          <div className="text-center py-16 text-zinc-400 text-sm">
            No files match your filters.
          </div>
        )}

        {/* Asset grid */}
        {!isLoading && !isError && pagedAssets.length > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {pagedAssets.map((asset) => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  onDelete={handleDelete}
                  onCopy={handleCopy}
                  copiedId={copiedId}
                  onClick={(id) => navigate(`/media/${id}`)}
                />
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} className="mt-4" />
          </>
        )}

        {/* Drag hint overlay */}
        {isDragging && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 pointer-events-none">
            <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-3">
              <Upload size={36} className="text-zinc-700" />
              <p className="text-lg font-semibold text-zinc-800">Drop to upload</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Embed Modal */}
      {embedModalOpen && (
        <AddEmbedModal
          onClose={() => setEmbedModalOpen(false)}
          onSaved={() => {
            setEmbedModalOpen(false);
            qc.invalidateQueries({ queryKey: ['media-assets'] });
          }}
        />
      )}
    </div>
  );
}
